import {useCallback, useState} from 'react';

/**
 * @typedef {Object} DateRange
 * @property {Date} startDate - The start date of the range
 * @property {Date} endDate - The end date of the range
 */

/**
 * @typedef {Object} UseFilterDatesReturn
 * @property {DateRange} date - The current date range
 * @property {import('react').Dispatch<import('react').SetStateAction<DateRange>>} setDate - Function to update the date range
 * @property {() => DateRange} resetDate - Function to reset the date range to default
 */

/**
 * Custom hook for managing a date range filter with a default number of days
 *
 * @param {number} defaultDays - The number of days to include in the default date range (inclusive)
 * @returns {UseFilterDatesReturn} An object containing:
 *   - date: An object with startDate and endDate
 *   - setDate: A setState function to update the date range
 *   - resetDate: A function to reset the date range to default values
 *
 * @example
 * const {date, setDate, resetDate} = useFilterDates(14);
 * // date = { startDate: Date (14 days ago), endDate: Date (today) }
 */
export function useFilterDates(defaultDays) {
  const resetDate = useCallback(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - (defaultDays - 1));
    return {
      startDate,
      endDate,
    };
  }, [defaultDays]);

  const [date, setDate] = useState(resetDate);

  return {date, setDate, resetDate};
}
